import { useState } from 'react';
import { useUniversitySearch } from '../hooks/useUniversitySearch.js';
import { UniversitySearchBar } from '../components/UniversitySearchBar.jsx';
import { UniversityResults } from '../components/UniversityResults.jsx';
import { ViewToggle } from '../components/ViewToggle.jsx';
import { DetailDrawer } from '../components/DetailDrawer.jsx';
import { InitialLoader, ResultsOverlay } from '../components/SearchLoader.jsx';

/**
 * Universities — a first-class search over the direct-contract universities,
 * the same collection the reference product reads.
 *
 * The headline count is a number of universities.
 */
function nameOf(list, id) {
  return (list ?? []).find((o) => o.id === id)?.name ?? id;
}

function chipsFor(filters, options, dispatch) {
  const chips = [];
  const add = (label, clear) => chips.push({ label, clear });

  if (filters.q) add(`"${filters.q}"`, () => dispatch({ type: 'SET_QUERY', value: '' }));
  for (const u of filters.universities) add(u, () => dispatch({ type: 'SET_UNIVERSITY', value: '' }));
  for (const c of filters.countries) add(c, () => dispatch({ type: 'SET_COUNTRY', value: '' }));
  for (const id of filters.intakeIds) {
    add(`Intake: ${nameOf(options?.intakeOptions, id)}`,
      () => dispatch({ type: 'TOGGLE', key: 'intakeIds', value: id }));
  }
  for (const id of filters.courseTypeIds) {
    add(nameOf(options?.courseTypes, id), () => dispatch({ type: 'SET_COURSE_TYPE', value: '' }));
  }
  for (const id of filters.campusIds) {
    add(`Location: ${nameOf(options?.campuses, id)}`, () => dispatch({ type: 'SET_LOCATION', value: '' }));
  }
  for (const id of filters.educationLevels) {
    add(`Qualification: ${nameOf(options?.highestQualifications, id)}`, () => dispatch({ type: 'SET_QUALIFICATION', value: '' }));
  }
  if (filters.gapYear) add(`Gap ≤ ${filters.gapYear}y`, () => dispatch({ type: 'SET_GAP_YEAR', value: '' }));
  if (filters.tuitionBelow15k) {
    add('Tuition below 15k', () => dispatch({ type: 'SET_FLAG', key: 'tuitionBelow15k', value: false }));
  }
  if (filters.interviewRequired) {
    add('Interview required', () => dispatch({ type: 'SET_FLAG', key: 'interviewRequired', value: false }));
  }
  if (filters.moiAccepted) {
    add('Accepts MOI', () => dispatch({ type: 'SET_FLAG', key: 'moiAccepted', value: false }));
  }
  return chips;
}

export default function UniversitiesPage() {
  const { filters, options, data, rows, error, loading, stale, dispatch, cleanupNote } = useUniversitySearch();
  const facets = data?.facets;

  // Which university card is open in the detail drawer — the whole search row.
  // The drawer fetches the safe MongoDB DTO by its id and reads a few
  // search-derived admission signals off the row. Search stays on Typesense.
  const [openUni, setOpenUni] = useState(null);

  // View state: 'card' (default) or 'list'
  const [view, setView] = useState('card');

  // Same rule as Search Programs: no count in the headline. The live,
  // query-driven total still has exactly one place — the result bar above the
  // cards (`.resbar`, from `data.found`) — where a change in it is a change in
  // what is actually showing.
  const title = 'Search in Universities';

  const chips = chipsFor(filters, options, dispatch);
  // Same rule as Search Programs: the result count is withheld until a filter
  // actually narrows something. `chips.length` is the same "is anything
  // filtered?" signal the chip row below already renders from.
  const hasFilters = chips.length > 0;

  return (
    <>
      {error && <div id="uni-error"><div className="err">{error}</div></div>}

      <UniversitySearchBar
        title={title}
        filters={filters}
        options={options}
        facets={facets}
        loading={loading}
        dispatch={dispatch}
      />

      <div className="chips" id="uni-chips">
        {chips.length === 0
          ? <span className="lab">No filters applied — showing every university</span>
          : (
            <>
              <span className="lab">Active:</span>
              {chips.map((c) => (
                <span className="chip" key={c.label} onClick={c.clear}>{c.label}<b>×</b></span>
              ))}
              <button type="button" className="clear" onClick={() => dispatch({ type: 'CLEAR_ALL' })}>
                Clear All
              </button>
            </>
          )}
      </div>

      <div className="results" style={{ width: '100%' }}>
        <div className="resbar">
          <div className="resbar-left">
            <div className="n" id="uni-count">
              {!data || stale ? '…'
                : hasFilters ? <><b>{data.found.toLocaleString()}</b> universities</>
                  : 'Showing all universities'}
            </div>
          </div>

          <div className="resbar-right">
            <ViewToggle view={view} onChange={setView} />
          </div>
        </div>

        {/* Same rule as Search Programs: `data` is null only until the first
            response for THIS search lands, so the initial-load branded loader
            covers exactly that gap. After that, a filter/search/sort/page
            change keeps the previous rows on screen (dimmed via `is-loading`)
            with the overlay on top, instead of the page ever looking empty. */}
        <div className={`results-area${data && loading ? ' is-loading' : ''}`}>
          <div id="uni-listWrap" aria-busy={data && loading ? 'true' : 'false'}>
            {!data
              ? (!error && <InitialLoader label="Loading universities…" view={view} />)
              : <UniversityResults rows={rows} found={data.found} onOpen={setOpenUni} view={view} />}
          </div>
          <ResultsOverlay active={Boolean(data) && loading} />
        </div>

        <div className="more" id="uni-more">
          {data && rows.length < data.found && (
            <button
              type="button"
              id="uni-loadMore"
              disabled={loading}
              aria-busy={loading}
              onClick={() => dispatch({ type: 'LOAD_MORE' })}
            >
              {loading
                ? 'Loading…'
                : `Load more (${rows.length.toLocaleString()} of ${data.found.toLocaleString()})`}
            </button>
          )}
        </div>
      </div>

      <DetailDrawer
        kind="universities"
        id={openUni?.id ?? null}
        hit={openUni}
        onClose={() => setOpenUni(null)}
      />

      {/* Same non-blocking, self-clearing notice as Search Programs — see
          useDismissingNote / filterDependency.js. */}
      {cleanupNote && (
        <div id="uni-filterCleanupNote" className="freshnote show" role="status">{cleanupNote}</div>
      )}
    </>
  );
}
